/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useEffect, useRef, useState } from 'react';

import { jsx } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import { cssMap, cx } from '@atlaskit/css';
import VideoStopOverlayIcon from '@atlaskit/icon/core/video-stop-overlay';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { RovoThinkingIndicator } from '@atlassian/rovo-platform-ui-components/loading-state/RovoThinkingIndicator';
import { RainbowProgressBar } from '@atlassian/rovo-platform-ui-components/primitives/RainbowProgressBar';
import {
	RovoGradientKeyframesStyle,
	ROVO_GRADIENT_BACKGROUND,
} from '@atlassian/rovo-platform-ui-components/rovo-gradient';

import type { RemixOptionKind } from '../../../index.ts';

import { useProgressFacade } from './use-progress-facade';

const messages = defineMessages({
	escHint: {
		id: 'malleable-ui-components.remix-thinking-bar.esc-hint',
		defaultMessage: 'Esc',
		description: 'Keyboard hint showing the Esc key can be used to cancel',
	},
	stopLabel: {
		id: 'malleable-ui-components.remix-thinking-bar.stop-label',
		defaultMessage: 'Stop remixing',
		description:
			'The text is used as the accessible label for the stop button displayed in the remix thinking bar while the AI is processing a remix operation. When clicked, it cancels the ongoing remixing action.',
	},
	rovoIsThinking: {
		id: 'malleable-ui-components.remix-thinking-bar.rotating.rovo-is-thinking',
		defaultMessage: 'Rovo is thinking',
		description: 'Initial loading message shown while Rovo is starting to generate a remix',
	},
	analyzingContent: {
		id: 'malleable-ui-components.remix-thinking-bar.rotating.analyzing-content',
		defaultMessage: 'Analyzing your content',
		description: 'Loading message shown while Rovo is analyzing the content for a remix',
	},
	puttingPiecesTogether: {
		id: 'malleable-ui-components.remix-thinking-bar.rotating.putting-pieces-together',
		defaultMessage: 'Putting the pieces together',
		description: 'Loading message shown while Rovo is assembling content for a remix',
	},
	structuringRemix: {
		id: 'malleable-ui-components.remix-thinking-bar.rotating.structuring-remix',
		defaultMessage: 'Structuring your remix',
		description: 'Loading message shown while Rovo is structuring the remix output',
	},
	designingRemix: {
		id: 'malleable-ui-components.remix-thinking-bar.rotating.designing-remix',
		defaultMessage: 'Designing your remix',
		description: 'Loading message shown while Rovo is designing the remix output',
	},
	addingItAllTogether: {
		id: 'malleable-ui-components.remix-thinking-bar.rotating.adding-it-all-together',
		defaultMessage: 'Adding it all together',
		description: 'Loading message shown while Rovo is combining all elements of the remix',
	},
	applyingFinalTouches: {
		id: 'malleable-ui-components.remix-thinking-bar.rotating.applying-final-touches',
		defaultMessage: 'Applying the final touches',
		description: 'Loading message shown while Rovo is finalizing the remix',
	},
	almostDone: {
		id: 'malleable-ui-components.remix-thinking-bar.rotating.almost-done',
		defaultMessage: 'Almost done',
		description: 'Loading message shown when Rovo is nearly finished generating a remix',
	},
});

const ROTATION_INTERVAL_MS = 4000;

/**
 * Cycles through `texts` at a fixed interval, stopping on the last item.
 * Only the initial array is used — the interval is set up once on mount.
 */
function useSequentialText(texts: string[]): string {
	const [index, setIndex] = useState(0);
	const indexRef = useRef(0);
	// Read the latest texts inside the interval without re-creating it each render.
	const textsRef = useRef(texts);
	textsRef.current = texts;

	useEffect(() => {
		if (textsRef.current.length <= 1) {
			return;
		}

		const interval = setInterval(() => {
			const next = indexRef.current + 1;
			if (next < textsRef.current.length) {
				indexRef.current = next;
				setIndex(next);
			} else {
				// Sequence exhausted — stay on the last message.
				clearInterval(interval);
			}
		}, ROTATION_INTERVAL_MS);

		return () => clearInterval(interval);
		// eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only; sequence is fixed for the component lifetime
	}, []);

	return textsRef.current[index] ?? textsRef.current[0] ?? '';
}

const styles = cssMap({
	gradientWrapper: {
		position: 'fixed',
		bottom: token('space.200'),
		left: token('space.050'),
		right: token('space.050'),
		width: 'fit-content',
		margin: '0 auto',
		zIndex: 600,
		borderRadius: token('radius.xxlarge'),
		animation: 'rovoGradientRotation linear 2s infinite',
	},
	gradientWrapperAbsolute: {
		position: 'absolute',
		bottom: token('space.200'),
		left: token('space.050'),
		right: token('space.050'),
		width: 'fit-content',
		margin: '0 auto',
		zIndex: 600,
		borderRadius: token('radius.xxlarge'),
		animation: 'rovoGradientRotation linear 2s infinite',
	},
	gradientInner: {
		paddingBlock: token('space.025'),
		paddingInline: token('space.025'),
		backgroundColor: token('elevation.surface.overlay'),
		borderRadius: 'inherit',
		backgroundClip: 'content-box',
		boxShadow: token('elevation.shadow.overlay'),
	},
	plainWrapper: {
		position: 'fixed',
		bottom: token('space.200'),
		left: token('space.050'),
		right: token('space.050'),
		width: 'fit-content',
		margin: '0 auto',
		zIndex: 600,
		borderRadius: token('radius.xxlarge'),
	},
	plainWrapperAbsolute: {
		position: 'absolute',
		bottom: token('space.200'),
		left: token('space.050'),
		right: token('space.050'),
		width: 'fit-content',
		margin: '0 auto',
		zIndex: 600,
		borderRadius: token('radius.xxlarge'),
	},
	plainInner: {
		backgroundColor: token('elevation.surface.overlay'),
		borderRadius: 'inherit',
		boxShadow: token('elevation.shadow.overlay'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
	},
	fixedWrapper: {
		paddingInline: token('space.200'),
		paddingBlock: token('space.100'),
		borderRadius: token('radius.xxlarge'),
		display: 'grid',
		gridTemplateColumns: 'auto 1fr auto',
		columnGap: token('space.100'),
		rowGap: token('space.050'),
		alignItems: 'center',
		minWidth: '400px',
	},
	fixedWrapperSingleRow: {
		gridTemplateRows: 'auto',
	},
	fixedWrapperWithProgress: {
		gridTemplateRows: 'auto auto',
	},
	iconCell: {
		gridColumn: '1',
		display: 'flex',
		alignItems: 'center',
	},
	iconCellSpanRows: {
		gridRow: '1 / 3',
	},
	textCell: {
		gridColumn: '2',
		gridRow: '1',
		minWidth: '0px',
	},
	progressBarCell: {
		gridColumn: '2',
		gridRow: '2',
		minWidth: '0px',
	},
	buttonCell: {
		gridColumn: '3',
		display: 'flex',
		alignItems: 'center',
	},
	buttonCellSpanRows: {
		gridRow: '1 / 3',
	},
	escBadge: {
		paddingInline: token('space.050'),
		paddingBlock: token('space.025'),
		backgroundColor: token('color.background.accent.gray.subtlest'),
		color: token('color.text.subtlest'),
		borderRadius: token('radius.small'),
		font: token('font.body.small'),
	},
});

type RemixThinkingBarProps = {
	onStop: () => void;
	optionType?: RemixOptionKind;
	/** Render a loading progress bar (and swap the animated border for a solid one). */
	showProgressBar?: boolean;
	/** When `showProgressBar`, rapidly fill to 100% then fire `onCompleteAnimationFinished`. */
	isComplete?: boolean;
	/** Fires once the progress bar finishes animating to 100%. */
	onCompleteAnimationFinished?: () => void;
	/**
	 * The content type of the document being edited (e.g. `'jira.issue'`, `'confluence.page'`).
	 * When the content type indicates an inline editor context (e.g. a Jira issue comment),
	 * the bar is positioned absolutely within the editor wrapper rather than fixed to the viewport.
	 */
	contentType?: string;
};

/**
 * A floating thinking bar that displays a Rovo loading animation, a sequentially
 * rotating status text, an Esc keyboard hint, and a stop button. Matches the
 * visual style of the inline chat ThinkingView / AI loading screen.
 *
 * The status text progresses through a fixed ordered sequence:
 * "Rovo is thinking" → "Analyzing your content" → "Putting the pieces together"
 * → "Structuring your remix" → "Designing your remix" → "Adding it all together"
 * → "Applying the final touches" → "Almost done"
 */
export function RemixThinkingBar({
	onStop,
	optionType: _optionType,
	showProgressBar = false,
	isComplete = false,
	onCompleteAnimationFinished,
	contentType,
}: RemixThinkingBarProps): JSX.Element {
	const positionAbsolute = contentType === 'jira.issue';
	const { formatMessage } = useIntl();

	const thinkingTexts = [
		formatMessage(messages.rovoIsThinking),
		formatMessage(messages.analyzingContent),
		formatMessage(messages.puttingPiecesTogether),
		formatMessage(messages.structuringRemix),
		formatMessage(messages.designingRemix),
		formatMessage(messages.addingItAllTogether),
		formatMessage(messages.applyingFinalTouches),
		formatMessage(messages.almostDone),
	];

	const thinkingTitle = useSequentialText(thinkingTexts);
	const progress = useProgressFacade({ isComplete, onCompleteAnimationFinished });

	return (
		<Box
			xcss={
				// eslint-disable-next-line no-nested-ternary
				showProgressBar
					? positionAbsolute
						? styles.plainWrapperAbsolute
						: styles.plainWrapper
					: positionAbsolute
						? styles.gradientWrapperAbsolute
						: styles.gradientWrapper
			}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-imported-style-values -- gradient background not expressible in cssMap
			style={showProgressBar ? undefined : { background: ROVO_GRADIENT_BACKGROUND }}
			testId="remix-thinking-bar-gradient-border"
		>
			{!showProgressBar && <RovoGradientKeyframesStyle />}
			<Box xcss={showProgressBar ? styles.plainInner : styles.gradientInner}>
				<Box
					xcss={cx(
						styles.fixedWrapper,
						showProgressBar ? styles.fixedWrapperWithProgress : styles.fixedWrapperSingleRow,
					)}
					testId="remix-thinking-bar"
				>
					<Box xcss={cx(styles.iconCell, showProgressBar && styles.iconCellSpanRows)}>
						<RovoThinkingIndicator title="" icon={true} showEllipsis={false} />
					</Box>
					<Box xcss={styles.textCell}>
						<RovoThinkingIndicator title={thinkingTitle} icon={false} />
					</Box>
					{showProgressBar && (
						<Box xcss={styles.progressBarCell}>
							<RainbowProgressBar
								testId="remix-thinking-progress-bar"
								progress={progress * 100}
								appearance="static-rainbow"
								label={formatMessage(messages.rovoIsThinking)}
							/>
						</Box>
					)}
					<Box xcss={cx(styles.buttonCell, showProgressBar && styles.buttonCellSpanRows)}>
						<Inline alignBlock="center" space="space.100">
							<Box as="span" xcss={styles.escBadge}>
								{formatMessage(messages.escHint)}
							</Box>
							<IconButton
								shape="circle"
								onClick={onStop}
								icon={(iconProps) => (
									<VideoStopOverlayIcon
										{...iconProps}
										color={token('color.icon.danger')}
										label=""
									/>
								)}
								label={formatMessage(messages.stopLabel)}
							/>
						</Inline>
					</Box>
				</Box>
			</Box>
		</Box>
	);
}
